import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FeatherMapPin } from "@subframe/core";
import { SettingsApiDataSource } from "../../data/datasources/SettingsApiDataSource";
import { SETTINGS_BASE_PATH } from "../config/settingsNavigation";

/**
 * ZOUR-65 phase 1 — "N locations · Manage" under the GSTIN on the GST page.
 *
 * The GST panel shows ONE GSTIN, because for most clients there is one. This
 * line is where a client with several registrations finds out that Zournal
 * knows, and where anyone else learns the concept exists before they meet it on
 * a bill.
 *
 * It reads the same endpoint the Locations page does, which also means opening
 * `/settings/gst` triggers the one-off first sync on a tenant that has never
 * pulled the list — the server decides that, not this component.
 *
 * FAILS SILENTLY, on purpose. The GST page's job is the GST registration date
 * and toggle. If locations cannot be read — expired token, migration not yet
 * run on this tenant — the right outcome is one missing line, not an error
 * banner on a page about something else.
 */
export const GstLocationsLine: React.FC = () => {
  const [count, setCount] = useState<number | null>(null);
  const [notSynced, setNotSynced] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    new SettingsApiDataSource()
      .fetchLocations(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setCount(data.count);
        setNotSynced(data.not_synced);
      })
      .catch(() => {
        // Deliberately swallowed — see the class comment.
      });

    return () => controller.abort();
  }, []);

  if (count === null) {
    return null;
  }

  return (
    <div className="flex w-full flex-wrap items-center gap-2 border-b border-solid border-neutral-border pb-8">
      <FeatherMapPin className="shrink-0 text-body font-body text-subtext-color" />
      <span className="text-body font-body text-default-font">
        {count} location{count === 1 ? "" : "s"}
      </span>
      <span className="text-body font-body text-subtext-color">·</span>
      <Link
        to={`${SETTINGS_BASE_PATH}/locations`}
        className="text-body font-body text-brand-700 hover:underline"
      >
        Manage →
      </Link>
      <span className="text-caption font-caption text-subtext-color">
        {notSynced
          ? "Not read from Zoho Books yet — open Locations to pull the list."
          : count === 1
          ? "One GST registration. A second one is added in Zoho Books."
          : "Each location carries its own GST registration."}
      </span>
    </div>
  );
};

export default GstLocationsLine;
